import React, { useState, useEffect, useContext } from 'react'
import { Col, message } from 'antd'
import { useGate } from 'statsig-react'

import { selectJob } from '@actions/jobList'
import { firebaseSetListener } from '../../../refactory/services/firebase/firebase'

import JobInfo from './job-info/job-info.component'
import Flags from './flags/flags.component'
import Tasks from './tasks/task.component'
import TimeLine from './time-line.component'
import { ChatWidgetV3 } from '../../../refactory/modules/jobs/infrastructure/job-detail/chat-widget.component'
import { Can } from '../../../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import { ModulePerms, SubModulePerms } from '../../../refactory/modules/access-control/domain/module'
import { HistoricalContext } from '../../../refactory/modules/jobs/infrastructure/job-detail/historical.context'
import { odinJobRepository } from '../../../refactory/modules/jobs/infrastructure/odin-job.repository'
import { getJobByUUIDUseCase } from '../../../refactory/modules/jobs/application/get-job-by-uuid.use-case'
import { FeaturesConfigurationContext } from '../../../refactory/modules/jobs/infrastructure/job-detail/features-configuration.context'

const CANCELLED = 'CANCELLED'
const CLIENTS = 'clients'
const JOBS = 'jobs'
const AGENTS = 'agents'
let unsubscribe = null

const JobDetail = ({
  dispatch,
  selectedJob,
  jobsSearch,
  currentUser,
  getJobsList,
  setSelectedJob,
  featureFlags,
  onCancel
}) => {
  const [job, setJob] = useState(null)
  const [timezone, setTimezone] = useState(null)
  const { getJobHistorical } = useContext(HistoricalContext)
  const { getCanUpdateTaskOptimals } = useContext(FeaturesConfigurationContext)
  const isGetDetailJobByNebulaActive = useGate('is_get_detail_job_by_nebula_active').value
  let timeoutRef = null

  useEffect(() => {
    if (job?.storeId) {
      getCanUpdateTaskOptimals(job.storeId)
    }
  }, [job?.storeId])

  const getJobDetail = async () => {
    const jobDetail = await getJobByUUIDUseCase(odinJobRepository)(job.uuid)
    setSelectedJob(jobDetail)
  }

  const isDelivered = () => {
    let delivered = false
    if (job && job.tasks && job.tasks.length > 0) {
      delivered = true
      job.tasks.forEach((task) => {
        delivered = delivered && task.status === 'FINISHED'
      })
    }
    return delivered
  }

  const getDeliveryCodeTries = () => {
    const { deliveryOptions = [] } = job || {}
    const deliveryCodeObject = deliveryOptions.find((item) => item && item.method === 'DELIVERY_CODE')
    if (deliveryCodeObject) {
      return deliveryCodeObject.deliveryCodeTries || 0
    }
    return 0
  }

  const cleanScreen = () => {
    if (unsubscribe) unsubscribe()
    timeoutRef = setTimeout(() => {
      selectJob(null, 0, dispatch)
      setJob(null)
    }, 100)
  }

  const startListenJob = (clientId, job, user) => {
    if (unsubscribe) unsubscribe()
    if (Can([SubModulePerms.FLA_GENERAL_ALL_SEE])) {
      startListenGeneralJob(clientId, job)
    } else {
      startListenAgentJob(clientId, job, user.id)
    }
  }

  const handleJobSnapshot = (snapshot) => {
    const firebaseJob = snapshot.data()
    if (firebaseJob) {
      setAllJob(firebaseJob)
    } else {
      message.info('No job found')
      cleanScreen()
    }
  }

  const startListenGeneralJob = (clientId, job) => {
    const path = `${CLIENTS}/${String(clientId)}/${JOBS}`
    unsubscribe = firebaseSetListener(path, job.key, handleJobSnapshot)
  }

  const startListenAgentJob = (clientId, job, agentId) => {
    const path = `${CLIENTS}/${String(clientId)}/${AGENTS}/${String(agentId)}/${JOBS}`
    unsubscribe = firebaseSetListener(path, job.key, handleJobSnapshot)
  }

  const setAllJob = (currentJob) => {
    const userInfo = JSON.parse(localStorage.getItem('user_info'))
    const currentTimeZone = currentJob.storeTimezone ? currentJob.storeTimezone : userInfo.user.client.timeZone

    setTimezone(currentTimeZone)
    if (job) {
      getJobHistorical(currentJob.id)
    }
    setJob((_) => currentJob)
  }

  useEffect(() => {
    if (!selectedJob) return

    if (!jobsSearch && currentUser) {
      const { user } = currentUser
      startListenJob(user.clientId, selectedJob, user)
    } else {
      setAllJob(selectedJob)
    }
    return () => {
      clearTimeout(timeoutRef)
    }
  }, [currentUser, dispatch, jobsSearch, selectedJob, selectedJob.jobNumber, selectedJob.timestamp])

  return (
    <React.Fragment>
      {job && (
        <>
          <Col className='job-container job-left-panel' span={jobsSearch ? 15 : 12}>
            <JobInfo
              job={job}
              setJob={setJob}
              isCancelled={job ? job.status === CANCELLED : false}
              jobsSearch={jobsSearch}
              setSelectedJob={isGetDetailJobByNebulaActive ? getJobDetail : setSelectedJob}
              getJobsList={getJobsList}
              isDelivered={isDelivered}
              currentUser={currentUser}
              featureFlags={featureFlags}
              store={job ? { ...job.origin, id: job.storeId } : null}
              tz={timezone}
            />

            {Can([ModulePerms.FLA]) ? <Flags tz={timezone} job={job} /> : null}

            <Tasks
              onCancel={onCancel}
              deliveryCodeTries={getDeliveryCodeTries()}
              deliveryCodeTriesLimit={job ? job.deliveryCodeTriesLimit || 3 : 5}
              tz={timezone}
              tasks={job ? job.tasks : null}
              jobId={job.id}
              store={job ? { ...job.origin, id: job.storeId } : null}
              setSelectedJob={isGetDetailJobByNebulaActive ? getJobDetail : setSelectedJob}
              isCancelled={job ? job.status === CANCELLED : false}
              isDelivered={isDelivered}
              jobsSearch={jobsSearch}
              currentUser={currentUser}
              operationModel={job?.operationModel || null}
              slotTo={job?.slotTo || null}
              forceDelivery={featureFlags?.forceDelivery === 'true' || false}
              job={job}
              setJob={setJob}
            />
          </Col>
          <Col className='job-right-panel' span={jobsSearch ? 8 : 6}>
            {Can([SubModulePerms.JOB_CHAT_SEE]) && (
              <ChatWidgetV3
                jobUuid={job.uuid}
                currentUserId={currentUser.user.id}
                currentUserName={`${currentUser.user.firstName} ${currentUser.user.lastName}`}
              />
            )}
            <TimeLine currentUser={currentUser} tz={timezone} jobId={job.id} />
          </Col>
        </>
      )}
    </React.Fragment>
  )
}

export default JobDetail
